import { useState, useEffect } from 'react';
import { BackButton } from './BackButton';
import { getAcademicYears, getAdvancedStats, getEvaluationPeriods, type CourseStats } from '../lib/db';
import type { AcademicYear, EvaluationPeriodConfig } from '../types';
import { BarChart3, TrendingUp, Users, Award } from 'lucide-react';

export function AdvancedStats() {
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [periods, setPeriods] = useState<EvaluationPeriodConfig[]>([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [stats, setStats] = useState<CourseStats[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([getAcademicYears(), getEvaluationPeriods()]).then(([years, p]) => {
      setAcademicYears(years);
      setPeriods(p);
      const active = years.find(y => y.isActive);
      if (active) setSelectedYear(active.name);
      if (p.length) setSelectedPeriod(p[0].key);
    });
  }, []);

  useEffect(() => {
    if (selectedYear && selectedPeriod) {
      setLoading(true);
      getAdvancedStats(selectedYear, selectedPeriod)
        .then(setStats)
        .finally(() => setLoading(false));
    }
  }, [selectedYear, selectedPeriod]);

  const totalStudents = stats.reduce((a, c) => a + c.studentCount, 0);
  const overallAvg = stats.length
    ? Math.round((stats.reduce((a, c) => a + c.averageGlobal * c.studentCount, 0) / Math.max(totalStudents, 1)) * 100) / 100
    : 0;
  const passRate = stats.length
    ? Math.round(
        (stats.reduce((a, c) => a + c.distribution.filter(d => !d.label.includes('Insuficiente')).reduce((s, d) => s + d.count, 0), 0) /
        Math.max(stats.reduce((a, c) => a + c.distribution.reduce((s, d) => s + d.count, 0), 0), 1)) * 100
      )
    : 0;

  const getBarColor = (label: string) => {
    if (label.includes('Sobresaliente')) return 'bg-emerald-500';
    if (label.includes('Notable')) return 'bg-blue-500';
    if (label.includes('Bien')) return 'bg-cyan-500';
    if (label.includes('Suficiente')) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="py-6">
      <div className="px-4">
        <div className="mb-6"><BackButton /></div>
        <div className="flex items-center gap-3 mb-8">
          <BarChart3 className="w-7 h-7 text-indigo-600" />
          <h1 className="text-2xl font-semibold text-gray-900">Estadísticas Avanzadas</h1>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Año Académico</label>
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              {academicYears.map(y => (
                <option key={y.id} value={y.name}>{y.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Periodo</label>
            <select
              value={selectedPeriod}
              onChange={e => setSelectedPeriod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              {periods.map(p => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-b-2 border-indigo-600 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-white p-5 rounded-xl shadow border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-indigo-500" />
                  <span className="text-sm font-medium text-gray-500">Total Alumnos</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">{totalStudents}</p>
              </div>
              <div className="bg-white p-5 rounded-xl shadow border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm font-medium text-gray-500">Media Global</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">{overallAvg}</p>
              </div>
              <div className="bg-white p-5 rounded-xl shadow border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <Award className="w-5 h-5 text-amber-500" />
                  <span className="text-sm font-medium text-gray-500">Tasa de Aprobados</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">{passRate}%</p>
              </div>
            </div>

            {/* Per-course breakdown */}
            <div className="space-y-6">
              {stats.filter(c => c.studentCount > 0).map(course => {
                const maxCount = Math.max(...course.distribution.map(d => d.count), 1);
                return (
                  <div key={course.courseId} className="bg-white rounded-xl shadow border border-gray-100 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-800">{course.courseName}</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{course.studentCount} alumnos</span>
                        <span className="font-medium text-indigo-600">Media: {course.averageGlobal}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {course.distribution.map(d => (
                        <div key={d.label} className="flex items-center gap-3">
                          <span className="text-xs text-gray-500 w-40 shrink-0">{d.label}</span>
                          <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${getBarColor(d.label)}`}
                              style={{ width: `${(d.count / maxCount) * 100}%`, minWidth: d.count > 0 ? '8px' : '0' }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700 w-8 text-right">{d.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {stats.every(c => c.studentCount === 0) && (
                <div className="text-center py-12 text-gray-400">
                  No hay datos de calificaciones para este periodo
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
